import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
  Plus,
  RotateCcw,
  FileText,
  MoreVertical,
  Receipt,
  Eye,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  LayoutGrid,
  List,
  XCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import { useApi } from "../../../hooks/useApi";
import { Skeleton } from "../../../components/ui/skeleton";
import RazorpayButton from "../../../components/RazorpayButton";
import { useAuth } from "../../../contexts/AuthContext";

export default function AllInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'grid'
  const [cancellingInvoiceId, setCancellingInvoiceId] = useState(null);
  const [activeActionsInvoiceId, setActiveActionsInvoiceId] = useState(null);
  const { get, post, patch, put } = useApi();
  const { hasRole, hasAnyRole, loading: authLoading, user } = useAuth();
  const adminId = user?.id || user?._id;

  // Set default view mode based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      setViewMode(isMobile ? "grid" : "table");
    };

    // Set initial view mode
    checkScreenSize();

    // Listen for resize events
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleRefresh = () => {
    fetchInvoices();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 text-primary" />
    ) : (
      <ChevronDown className="h-4 w-4 text-primary" />
    );
  };

  const fetchInvoices = useCallback(async () => {
    if (!adminId) {
      return;
    }

    try {
      setIsLoading(true);

      // Try different API endpoints to see which one works
      let response;
      const queryParams = new URLSearchParams({
        admin_id: adminId,
        search: "",
        sortBy: "createdAt",
        sortOrder: "desc",
      }).toString();

      try {
        // First try the full endpoint with parameters
        response = await get(`/invoices?${queryParams}`);
      } catch (firstError) {
        try {
          // Try basic endpoint
          response = await get(`/invoices?${queryParams}`);
        } catch (secondError) {
          
          // Try admin endpoint
          response = await get(`/admin/invoices?${queryParams}`);
        }
      }

      // Handle different response structures
      if (response?.success && Array.isArray(response?.data)) {
        setInvoices(response.data);
      } else if (Array.isArray(response)) {
        setInvoices(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setInvoices(response.data);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      
      toast.error(
        "Failed to fetch invoices: " + (error.message || "Unknown error")
      );
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  }, [adminId, get]);

  const handleCancelInvoice = useCallback(
    async (invoice) => {
      const invoiceId = invoice?.id || invoice?._id;

      if (!invoiceId) {
        toast.error("Unable to cancel invoice. Missing invoice ID.");
        return;
      }

      setCancellingInvoiceId(invoiceId);

      const payload = {
        status: "cancelled",
        admin_id: adminId,
        invoice_id: invoiceId,
      };

      const cancelEndpoints = [
        { url: `/invoices/${invoiceId}/status`, method: "PUT" },
      ];

      try {
        let response = null;

        for (const endpoint of cancelEndpoints) {
          try {
            if (endpoint.method === "post") {
              response = await post(endpoint.url, payload);
            } else {
              response = await put(endpoint.url, payload);
            }
            break;
          } catch (error) {
            
          }
        }

        if (!response) {
          throw new Error("All cancel attempts failed. Please try again.");
        }

        setInvoices((prev) =>
          prev.map((inv) => {
            const currentId = inv.id || inv._id;
            if (currentId === invoiceId) {
              return { ...inv, status: "cancelled" };
            }
            return inv;
          })
        );

        toast.success(response?.message || "Invoice cancelled successfully.");
      } catch (error) {
        toast.error(error.message || "Failed to cancel invoice.");
      } finally {
        setCancellingInvoiceId(null);
      }
    },
    [adminId, put, post]
  );

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN");
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      invoice.invoiceNumber?.toLowerCase().includes(searchLower) ||
      invoice.account?.name?.toLowerCase().includes(searchLower) ||
      invoice.account?.email?.toLowerCase().includes(searchLower) ||
      invoice.quoteNumber?.toLowerCase().includes(searchLower)
    );
  });

  // Sort the filtered invoices
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case "createdAt":
        aValue = new Date(a.createdAt || a.issueDate || 0);
        bValue = new Date(b.createdAt || b.issueDate || 0);
        break;
      case "issueDate":
        aValue = new Date(a.issueDate || 0);
        bValue = new Date(b.issueDate || 0);
        break;
      case "invoiceNumber":
        aValue = (a.invoiceNumber || "").toLowerCase();
        bValue = (b.invoiceNumber || "").toLowerCase();
        break;
      case "customer":
        aValue = (a.account?.name || "").toLowerCase();
        bValue = (b.account?.name || "").toLowerCase();
        break;
      case "quoteNumber":
        aValue = (a.quoteNumber || "").toLowerCase();
        bValue = (b.quoteNumber || "").toLowerCase();
        break;
      case "amount":
        aValue = parseFloat(a.total || 0);
        bValue = parseFloat(b.total || 0);
        break;
      case "status":
        aValue = (a.status || "").toLowerCase();
        bValue = (b.status || "").toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvoices = sortedInvoices.slice(startIndex, endIndex);

  const statusBadgeClasses = {
    paid: "bg-green-100 text-green-700 border border-green-200",
    draft: "bg-orange-100 text-orange-700 border border-orange-200",
    cancelled: "bg-red-100 text-red-700 border border-red-200",
    cancel: "bg-red-100 text-red-700 border border-red-200",
    approved: "bg-green-100 text-green-700 border border-green-200",
    sent: "bg-blue-100 text-blue-700 border border-blue-200",
  };

  const getStatusBadgeClass = (status) => {
    const normalizedStatus = (status || "").toLowerCase();
    return (
      statusBadgeClasses[normalizedStatus] ||
      "bg-muted text-muted-foreground border border-muted-foreground/30"
    );
  };

  if (authLoading || isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>

        <Card className="border-1 border-muted-foreground/20">
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allowedRoles = ["admin", "superadmin", "super_admin", "super-admin"];
  const normalizedRole = (user?.role || "").toLowerCase();
  const superAdminRoles = ["superadmin", "super_admin", "super-admin"];
  const isSuperAdmin = superAdminRoles.includes(normalizedRole);
  const canViewInvoices =
    (typeof hasAnyRole === "function" && hasAnyRole(allowedRoles)) ||
    allowedRoles.some((role) => role.toLowerCase() === normalizedRole) ||
    (typeof hasRole === "function" && hasRole("admin"));

  if (!canViewInvoices) {
    return (
      <div className="p-4">
        <Card className="max-w-lg mx-auto">
          <CardHeader>
            <CardTitle>Restricted Access</CardTitle>
            <CardDescription>
              You do not have permission to view invoices.
            </CardDescription>
          </CardHeader>
          <CardContent>
            Please sign in with an admin account to access this page.
          </CardContent>
        </Card>
      </div>
    );
  }

  // Skeleton loading component
  const SkeletonRow = () => (
    <TableRow className="h-8">
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden lg:table-cell">
        <Skeleton className="h-6 w-20 rounded-full" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-6 w-16 rounded-full" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      {activeActionsInvoiceId && (
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-40 pointer-events-none" />
      )}
      <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen relative">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Invoices</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <div className="relative w-full sm:w-64">
              <Input
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search invoices..."
                className="pl-9 pr-3 h-9 bg-muted/50 border-1 border-muted-foreground/20 text-sm"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setViewMode(viewMode === "table" ? "grid" : "table")
              }
              className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
            >
              {viewMode === "table" ? (
                <LayoutGrid className="h-4 w-4" />
              ) : (
                <List className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {viewMode === "table" ? "Grid" : "Table"}
              </span>
            </Button>
          </div>
        </div>

        {/* Table or Grid View */}
        {viewMode === "table" ? (
          <div className="bg-card text-card-foreground rounded-sm overflow-x-auto border-1 border-muted-foreground/20">
            <Table className="text-xs sm:text-sm min-w-full border-1 border-muted-foreground/20">
              <TableHeader className="bg-muted/70 border-1 border-muted-foreground/20">
                <TableRow className="h-8 bg-muted/50">
                  <TableHead
                    className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[120px] hidden md:table-cell cursor-pointer hover:bg-muted/70 select-none"
                    onClick={() => handleSort("issueDate")}
                  >
                    <div className="flex items-center gap-1">
                      Issue Date
                      {getSortIcon("issueDate")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px] cursor-pointer hover:bg-muted/70 select-none"
                    onClick={() => handleSort("invoiceNumber")}
                  >
                    <div className="flex items-center gap-1">
                      Invoice Number
                      {getSortIcon("invoiceNumber")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[140px] cursor-pointer hover:bg-muted/70 select-none"
                    onClick={() => handleSort("customer")}
                  >
                    <div className="flex items-center gap-1">
                      Customer
                      {getSortIcon("customer")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[140px] sm:min-w-[170px] hidden sm:table-cell cursor-pointer hover:bg-muted/70 select-none"
                    onClick={() => handleSort("quoteNumber")}
                  >
                    <div className="flex items-center gap-1">
                      Quote Number
                      {getSortIcon("quoteNumber")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px] cursor-pointer hover:bg-muted/70 select-none"
                    onClick={() => handleSort("amount")}
                  >
                    <div className="flex items-center gap-1">
                      Amount
                      {getSortIcon("amount")}
                    </div>
                  </TableHead>

                  <TableHead
                    className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px] cursor-pointer hover:bg-muted/70 select-none"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {getSortIcon("status")}
                    </div>
                  </TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">
                    Actions
                  </TableHead>
                  {!isSuperAdmin && (
                    <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] hidden lg:table-cell">
                      Payment
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Show skeleton rows while loading
                  Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={`skeleton-${index}`} />
                  ))
                ) : sortedInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">
                        No invoices found
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {searchTerm
                          ? "Try adjusting your search criteria"
                          : "Get started by creating your first invoice"}
                      </p>
                      {!searchTerm && (
                        <Button asChild>
                          <Link to="/admin/invoices/add">
                            <Plus className="h-4 w-4 mr-2" />
                            Create Invoice
                          </Link>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  // Show actual data when loaded
                  currentInvoices.map((invoice) => {
                    const invoiceStatus = (invoice.status || "").toLowerCase();
                    const isInvoicePaid = invoiceStatus === "paid";
                    const invoiceId = invoice.id || invoice._id;

                    return (
                      <TableRow key={invoiceId} className="h-8">
                        <TableCell className="px-2 sm:px-3 py-2 text-xs sm:text-sm hidden md:table-cell">
                          {formatDate(invoice.issueDate)}
                        </TableCell>
                        <TableCell className="px-2 sm:px-3 py-2 cursor-pointer hover:text-primary">
                          <span className="text-xs sm:text-sm font-medium">
                            {invoice.invoiceNumber}
                          </span>
                        </TableCell>
                        <TableCell className="px-2 sm:px-3 py-2">
                          <div>
                            <div className="text-xs sm:text-sm font-medium truncate max-w-[100px] sm:max-w-[120px]">
                              {invoice.account?.name || "N/A"}
                            </div>
                            <div className="text-xs text-muted-foreground truncate max-w-[100px] sm:max-w-[120px]">
                              {invoice.account?.email || "N/A"}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
                          <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[140px]">
                            {invoice.quoteNumber || "N/A"}
                          </span>
                        </TableCell>
                        <TableCell className="px-2 sm:px-3 py-2">
                          <div>
                            <div className="text-xs sm:text-sm font-medium">
                              {formatCurrency(parseFloat(invoice.total || 0))}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Tax:{" "}
                              {formatCurrency(
                                parseFloat(invoice.totalTax || 0)
                              )}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="px-2 sm:px-3 py-2">
                          <Badge
                            className={`text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-full ${getStatusBadgeClass(
                              invoice.status
                            )}`}
                          >
                            {invoice.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-2 sm:px-3 py-2">
                          <div className="flex flex-col gap-2">
                            {!isSuperAdmin && (
                              <div className="lg:hidden">
                                {isInvoicePaid ? (
                                  <span className="text-xs font-semibold text-green-600">
                                    Payment Done
                                  </span>
                                ) : (
                                  <RazorpayButton
                                    amount={Number(invoice.total) || 0}
                                    userName={invoice.account?.name}
                                    userEmail={invoice.account?.email}
                                    userContact={invoice.account?.phone}
                                    invoiceId={invoiceId}
                                    adminId={user?.id || user?._id}
                                    buttonClassName="w-full"
                                  />
                                )}
                              </div>
                            )}
                            <DropdownMenu
                              onOpenChange={(open) =>
                                setActiveActionsInvoiceId(
                                  open ? invoiceId : null
                                )
                              }
                            >
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 sm:h-8 sm:w-8 p-0"
                                >
                                  <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="w-48 z-50 border-1 border-muted-foreground/20"
                              >
                                {isSuperAdmin && invoiceStatus === "draft" && (
                                  <DropdownMenuItem
                                    onClick={() => handleCancelInvoice(invoice)}
                                    disabled={cancellingInvoiceId === invoiceId}
                                    className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-700"
                                  >
                                    <XCircle className="h-4 w-4" />
                                    {cancellingInvoiceId === invoiceId
                                      ? "Cancelling..."
                                      : "Cancel"}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => {
                                    // Navigate to invoice details page
                                    window.location.href = `/admin/invoices/${invoiceId}/details`;
                                  }}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <Eye className="h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    // Navigate to invoice details
                                    window.location.href = `/admin/invoices/${invoiceId}`;
                                  }}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <Receipt className="h-4 w-4" />
                                  View Invoice
                                </DropdownMenuItem>
                                {/* Only show Generate Invoice if no quote number exists */}
                                {!invoice.quoteNumber && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      // Navigate to create new invoice page
                                      window.location.href =
                                        "/admin/invoices/add";
                                    }}
                                    className="flex items-center gap-2 cursor-pointer"
                                  >
                                    <Plus className="h-4 w-4" />
                                    Generate Invoice
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                        {!isSuperAdmin && (
                          <TableCell className="px-2 sm:px-3 py-2 hidden lg:table-cell">
                            {isInvoicePaid ? (
                              <span className="text-xs font-semibold text-green-600">
                                Payment Done
                              </span>
                            ) : (
                              <RazorpayButton
                                amount={Number(invoice.total) || 0}
                                userName={invoice.account?.name}
                                userEmail={invoice.account?.email}
                                userContact={invoice.account?.phone}
                                invoiceId={invoiceId}
                                adminId={user?.id || user?._id}
                                label="Pay Now"
                                buttonClassName="px-3 py-1 text-xs rounded-full"
                              />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {isLoading ? (
              /* Skeleton Grid */
              Array.from({ length: 8 }).map((_, index) => (
                <Card
                  key={`skeleton-${index}`}
                  className="border-1 border-muted-foreground/20"
                >
                  <CardContent className="p-4 space-y-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </CardContent>
                </Card>
              ))
            ) : currentInvoices.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  No invoices found
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm
                    ? "Try adjusting your search criteria"
                    : "Get started by creating your first invoice"}
                </p>
                {!searchTerm && (
                  <Button asChild>
                    <Link to="/admin/invoices/add">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Invoice
                    </Link>
                  </Button>
                )}
              </div>
            ) : (
              currentInvoices.map((invoice) => {
                const invoiceStatus = (invoice.status || "").toLowerCase();
                const isInvoicePaid = invoiceStatus === "paid";
                const invoiceId = invoice.id || invoice._id;
                return (
                  <Card
                    key={invoiceId}
                    className="border-1 border-muted-foreground/20 hover:shadow-lg transition-all duration-300 hover:border-primary/20 group bg-muted/50"
                  >
                    <CardContent className="p-4 space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-1">
                            Invoice Number
                          </p>
                          <p className="text-sm font-semibold truncate">
                            {invoice.invoiceNumber}
                          </p>
                        </div>
                        <DropdownMenu
                          onOpenChange={(open) =>
                            setActiveActionsInvoiceId(open ? invoiceId : null)
                          }
                        >
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-48 z-50 border-1 borbg-muted/70der-muted-foreground/20"
                          >
                            {isSuperAdmin && invoiceStatus === "draft" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleCancelInvoice(invoice)}
                                  disabled={cancellingInvoiceId === invoiceId}
                                  className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-700"
                                >
                                  <XCircle className="h-4 w-4" />
                                  {cancellingInvoiceId === invoiceId
                                    ? "Cancelling..."
                                    : "Cancel"}
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() =>
                                (window.location.href = `/admin/invoices/${invoiceId}/details`)
                              }
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <Eye className="h-4 w-4" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                (window.location.href = `/admin/invoices/${invoiceId}`)
                              }
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <Receipt className="h-4 w-4" />
                              View Invoice
                            </DropdownMenuItem>
                            {!invoice.quoteNumber && (
                              <DropdownMenuItem
                                onClick={() =>
                                  (window.location.href = "/admin/invoices/add")
                                }
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <Plus className="h-4 w-4" />
                                Generate Invoice
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Customer */}
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Customer
                        </p>
                        <p className="text-sm font-medium truncate">
                          {invoice.account?.name || "N/A"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {invoice.account?.email || "N/A"}
                        </p>
                      </div>

                      {/* Quote Number */}
                      {invoice.quoteNumber && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">
                            Quote Number
                          </p>
                          <p className="text-sm truncate">
                            {invoice.quoteNumber}
                          </p>
                        </div>
                      )}

                      {/* Amount */}
                      <div className="flex items-baseline justify-between pt-2 border-t border-muted-foreground/10">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">
                            Total Amount
                          </p>
                          <p className="text-lg font-bold">
                            {formatCurrency(parseFloat(invoice.total || 0))}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground mb-1">
                            Tax
                          </p>
                          <p className="text-xs font-medium">
                            {formatCurrency(parseFloat(invoice.totalTax || 0))}
                          </p>
                        </div>
                      </div>

                      {/* Date & Status */}
                      <div className="flex items-center justify-between pt-2 border-t border-muted-foreground/10">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(invoice.issueDate)}
                          </p>
                        </div>
                        <Badge
                          className={`text-[11px] font-semibold px-3 py-1 rounded-full ${getStatusBadgeClass(
                            invoice.status
                          )}`}
                        >
                          {invoice.status}
                        </Badge>
                      </div>

                      {/* Payment (Mobile/Grid view) */}
                      {!isSuperAdmin && (
                        <div className="pt-2 border-t border-muted-foreground/10">
                          {isInvoicePaid ? (
                            <span className="text-xs font-semibold text-green-600">
                              Payment Done
                            </span>
                          ) : (
                            <RazorpayButton
                              amount={Number(invoice.total) || 0}
                              userName={invoice.account?.name}
                              userEmail={invoice.account?.email}
                              userContact={invoice.account?.phone}
                              invoiceId={invoiceId}
                              adminId={adminId}
                              label="Pay Now"
                              buttonClassName="w-full text-xs sm:text-sm mt-2"
                            />
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4">
          {isLoading ? (
            <>
              <Skeleton className="h-5 w-48" />
              <div className="flex items-center gap-2 flex-wrap">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-8 w-12" />
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-muted-foreground">
                Showing page {currentPage} of {totalPages} | Total:{" "}
                {sortedInvoices.length} invoices
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="hover:bg-muted/50"
                >
                  Previous
                </Button>
                {/* Show page 1 if not in range */}
                {currentPage > 2 && totalPages > 3 && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      className={`${
                        currentPage === 1 
                          ? '!bg-black !text-white dark:!bg-white dark:!text-black font-semibold hover:!bg-black/90 dark:hover:!bg-white/90 border-black dark:border-white' 
                          : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      1
                    </Button>
                    {currentPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
                  </>
                )}
                {/* Show pages around current page */}
                {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return page;
                })
                .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
                .filter(page => page >= 1 && page <= totalPages) // Ensure valid range
                .map(page => (
                  <Button
                    key={page}
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className={`${
                      currentPage === page 
                        ? '!bg-black !text-white dark:!bg-white dark:!text-black font-semibold hover:!bg-black/90 dark:hover:!bg-white/90 border-black dark:border-white' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {page}
                  </Button>
                ))}
                {/* Show last page if not in range */}
                {currentPage < totalPages - 2 && totalPages > 3 && (
                  <>
                    {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      className={`${
                        currentPage === totalPages 
                          ? '!bg-black !text-white dark:!bg-white dark:!text-black font-semibold hover:!bg-black/90 dark:hover:!bg-white/90 border-black dark:border-white' 
                          : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="hover:bg-muted/50"
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
